import React, { useState, useCallback, useMemo, useRef } from 'react';
import { Box, Typography, Collapse, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { HeatmapTimelineData, HeatmapSection, HeatmapRowData, HeatmapBarSegment, TimelineDay } from '../types';
import { findTodayIndex } from '../helpers';
import { useAutoScrollToToday } from '../useAutoScrollToToday';
import HeatmapTimeline from './HeatmapTimeline';
import HeatmapRow from './HeatmapRow';
import HeatmapGridColumns from './HeatmapGridColumns';
import TodayColumnHighlight from './TodayColumnHighlight';

const LABEL_WIDTH = 200;
const ROW_HEIGHT = 38;
const borderDivider = { borderBottom: 1, borderColor: 'divider' as const };

const defaultRenderRowLabel = (row: HeatmapRowData) => (
  <Typography variant="body1" color="textSecondary">
    {row.label}
  </Typography>
);

const NameCell = React.memo<{
  row: HeatmapRowData;
  renderRowLabel?: (row: HeatmapRowData) => React.ReactNode;
}>(({ row, renderRowLabel = defaultRenderRowLabel }) => (
  <Box
    height={ROW_HEIGHT}
    display="flex"
    alignItems="center"
    justifyContent="flex-end"
    minWidth={0}
    pr={1}
    title={row.label}
  >
    <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" textAlign="right" minWidth={0} width="100%">
      {renderRowLabel(row)}
    </Box>
  </Box>
));

const HeatmapLabelColumn = React.memo<{
  sections: HeatmapSection[];
  collapsed: Record<string, boolean>;
  onToggleSection: (sectionId: string) => void;
  renderRowLabel?: (row: HeatmapRowData) => React.ReactNode;
}>(({ sections, collapsed, onToggleSection, renderRowLabel }) => (
  <Box width={LABEL_WIDTH} flexShrink={0}>
    <Box height="33px" mt="5px" />
    {sections.map(section => (
      <Box key={section.id} width="100%" mb="1px" sx={{ ...borderDivider }}>
        <Box height={ROW_HEIGHT} display="flex" alignItems="center">
          {section.collapsible && (
            <IconButton size="small" onClick={() => onToggleSection(section.id)} sx={{ p: 0, mr: '2px', ml: '-2px' }}>
              <ExpandMoreIcon
                sx={{
                  fontSize: 16,
                  transform: collapsed[section.id] ? 'rotate(-180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            </IconButton>
          )}

          <Typography variant="subtitle1" color="textPrimary" ml={section.collapsible ? 0 : 2}>
            {section.title}
          </Typography>
        </Box>

        {section.collapsible && section.rows && (
          <Collapse in={!collapsed[section.id]}>
            {section.rows.map(row => (
              <NameCell key={row.id} row={row} renderRowLabel={renderRowLabel} />
            ))}
          </Collapse>
        )}
      </Box>
    ))}
  </Box>
));

const HeatmapRowWithColumns = React.memo<{
  days: TimelineDay[];
  bars?: HeatmapBarSegment[];
  hasTopBorder?: boolean;
  hasBottomBorder?: boolean;
  barHeight?: number;
}>(({ days, bars, hasTopBorder, hasBottomBorder, barHeight = 5 }) => (
  <Box height={ROW_HEIGHT} position="relative" display="flex" alignItems="center">
    <HeatmapGridColumns days={days} hasTopBorder={hasTopBorder} hasBottomBorder={hasBottomBorder} />

    <HeatmapRow days={days} bars={bars ?? []} barHeight={barHeight} />
  </Box>
));

interface ProjectHeatmapProps {
  data: HeatmapTimelineData;
  renderRowLabel?: (row: HeatmapRowData) => React.ReactNode;
}

const ProjectHeatmap: React.FC<ProjectHeatmapProps> = ({ data, renderRowLabel }) => {
  const { days, sections } = data;
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const toggleSection = useCallback((sectionId: string) => {
    setCollapsed(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  }, []);

  const scrollableRef = useRef<HTMLDivElement>(null);
  const todayIndex = useMemo(() => findTodayIndex(days), [days]);

  useAutoScrollToToday(todayIndex, days, scrollableRef);

  return (
    <Box display="flex">
      <HeatmapLabelColumn
        sections={sections}
        collapsed={collapsed}
        onToggleSection={toggleSection}
        renderRowLabel={renderRowLabel}
      />

      <Box
        ref={scrollableRef}
        flexGrow={1}
        flexShrink={0}
        minWidth={0}
        maxWidth={`calc(100% - ${LABEL_WIDTH}px)`}
        p={0.25}
        overflow="auto"
        sx={{ contain: 'inline-size' }}
      >
        <Box
          mb="10px"
          pb="1px"
          minWidth="100%"
          pr="5px"
          pt="5px"
          width="fit-content"
          position="relative"
          sx={{
            ...borderDivider,
          }}
        >
          <TodayColumnHighlight todayIndex={todayIndex} />

          <HeatmapTimeline days={days} />

          <Box position="relative" mt="1px">
            {sections.map((section, sectionIndex) => (
              <Box key={section.id} width="100%">
                {sectionIndex > 0 && <Box sx={borderDivider} />}

                <HeatmapRowWithColumns
                  days={days}
                  bars={section.summaryBars}
                  hasTopBorder={sectionIndex === 0}
                  hasBottomBorder={Boolean(collapsed[section.id])}
                  barHeight={5}
                />

                {section.collapsible && section.rows && (
                  <Collapse in={!collapsed[section.id]}>
                    {section.rows.map((row, rowIndex) => (
                      <HeatmapRowWithColumns
                        key={row.id}
                        days={days}
                        bars={row.bars}
                        hasBottomBorder={!collapsed[section.id] && rowIndex === (section.rows?.length ?? 0) - 1}
                        barHeight={10}
                      />
                    ))}
                  </Collapse>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectHeatmap;
